(function () {
  'use strict';

  const FILTERS = [
    { key: 'variationFilters', label: 'Variation' }
  ];

  const ICONS = {
    play: '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="m8 5 11 7-11 7z"></path></svg>',
    copy: '<svg aria-hidden="true" viewBox="0 0 24 24"><rect x="8" y="8" width="11" height="11" rx="2"></rect><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"></path></svg>',
    link: '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="M10 13a5 5 0 0 0 7.54.54l2-2a5 5 0 0 0-7.07-7.07l-1.15 1.14"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-2 2a5 5 0 0 0 7.07 7.07l1.14-1.14"></path></svg>'
  };

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const normalize = (value) => String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[°˚]/g, ' degrees ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  class KiloExerciseLibrary {
    constructor(root) {
      this.root = root;
      this.data = this.readData();
      this.access = root.dataset.access || 'preview';
      this.fullCounts = this.readCounts();
      this.filterCatalogue = this.readFilterCatalogue();
      this.pageSize = Number(root.dataset.pageSize) || 24;
      this.visibleLimit = this.pageSize;
      this.query = '';
      this.region = 'All Exercises';
      this.sort = 'source';
      this.selected = Object.fromEntries(FILTERS.map((filter) => [filter.key, new Set()]));
      this.filtered = [];
      this.filterDefinitions = this.createFilterDefinitions();

      this.elements = {
        search: root.querySelector('[data-library-search]'),
        clearSearch: root.querySelector('[data-clear-search]'),
        regionTabs: root.querySelector('[data-region-tabs]'),
        filterPanel: root.querySelector('[data-filter-panel]'),
        filterGroups: root.querySelector('[data-filter-groups]'),
        filterToggle: root.querySelector('[data-filter-toggle]'),
        filterClose: root.querySelector('[data-filter-close]'),
        activeFilterCount: root.querySelector('[data-active-filter-count]'),
        clearFilters: root.querySelector('[data-clear-filters]'),
        activeFilters: root.querySelector('[data-active-filters]'),
        sort: root.querySelector('[data-library-sort]'),
        count: root.querySelector('[data-results-count]'),
        heading: root.querySelector('[data-results-heading]'),
        results: root.querySelector('[data-library-results]'),
        pdfDownload: root.querySelector('[data-pdf-download]'),
        empty: root.querySelector('[data-empty-state]'),
        reset: root.querySelector('[data-reset-library]'),
        loadMoreWrap: root.querySelector('[data-load-more-wrap]'),
        loadMore: root.querySelector('[data-load-more]')
      };

      if (!this.data.length || !this.elements.results) return;
      this.renderRegions();
      this.renderFilterGroups();
      this.bindEvents();
      this.render();
    }

    readData() {
      const records = [];
      this.root.querySelectorAll('[data-exercise-library-chunk]').forEach((chunk) => {
        try {
          const parsed = JSON.parse(chunk.textContent.trim());
          if (Array.isArray(parsed)) records.push(...parsed);
        } catch (error) {
          console.error('KILO Exercise Library data could not be loaded.', error);
        }
      });
      return records.map((record, index) => ({ ...record, sourceIndex: index }));
    }

    readCounts() {
      const counts = this.root.querySelector('[data-exercise-library-counts]');
      if (!counts) return {};
      try {
        const parsed = JSON.parse(counts.textContent.trim());
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (error) {
        console.error('KILO Exercise Library counts could not be loaded.', error);
        return {};
      }
    }

    readFilterCatalogue() {
      const catalogue = this.root.querySelector('[data-exercise-library-filters]');
      if (!catalogue) return [];
      try {
        const parsed = JSON.parse(catalogue.textContent.trim());
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error('KILO Exercise Library filter catalogue could not be loaded.', error);
        return [];
      }
    }

    createFilterDefinitions() {
      const families = [...new Set(this.data.map((record) => record.family).filter(Boolean))];
      return FILTERS.map((filter) => {
        const counts = new Map();
        this.data.forEach((record) => {
          const values = Array.isArray(record[filter.key]) ? record[filter.key] : [record[filter.key]];
          values.filter(Boolean).forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));
        });
        if (this.access !== 'full' && filter.key === 'variationFilters' && this.filterCatalogue.length) {
          const options = this.filterCatalogue.map((option) => ({
            ...option,
            fullCount: option.count,
            count: counts.get(option.value) || 0
          }));
          return { ...filter, options };
        }
        const options = [...counts.entries()]
          .map(([value, count]) => {
            const parent = families.find((family) => value !== family && value.startsWith(`${family} - `));
            return {
              value,
              count,
              label: parent ? value.slice(parent.length + 3) : value,
              isSubcategory: Boolean(parent),
              parent: parent || ''
            };
          });
        return { ...filter, options };
      });
    }

    bindEvents() {
      let searchTimer;
      this.elements.search?.addEventListener('input', (event) => {
        window.clearTimeout(searchTimer);
        this.query = event.target.value.trim();
        if (this.query) {
          this.clearFilterSelections();
          this.region = 'All Exercises';
          this.renderRegions();
        }
        this.visibleLimit = this.pageSize;
        this.elements.clearSearch.hidden = !this.query;
        searchTimer = window.setTimeout(() => this.render(), 80);
      });

      this.elements.clearSearch?.addEventListener('click', () => {
        this.clearSearchState(true);
        this.visibleLimit = this.pageSize;
        this.render();
      });

      this.elements.regionTabs?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-region]');
        if (!button) return;
        if (this.query) this.clearSearchState(false);
        if (this.region !== button.dataset.region) {
          this.clearFilterSelections();
        }
        this.region = button.dataset.region;
        this.visibleLimit = this.pageSize;
        this.renderRegions();
        this.render();
      });

      this.elements.filterGroups?.addEventListener('change', (event) => {
        const input = event.target.closest('[data-filter-key]');
        if (!input) return;
        if (this.query) this.clearSearchState(false);
        const key = input.dataset.filterKey;
        const set = this.selected[key];
        if (key === 'variationFilters') {
          this.updateVariationSelection(input, set);
        } else {
          input.checked ? set.add(input.value) : set.delete(input.value);
        }
        this.visibleLimit = this.pageSize;
        this.render();
      });

      this.elements.activeFilters?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-remove-filter]');
        if (!button) return;
        const key = button.dataset.filterKey;
        const value = button.dataset.filterValue;
        this.selected[key]?.delete(value);
        const input = this.elements.filterGroups.querySelector(
          `[data-filter-key="${CSS.escape(key)}"][value="${CSS.escape(value)}"]`
        );
        if (input) input.checked = false;
        this.visibleLimit = this.pageSize;
        this.render();
      });

      this.elements.clearFilters?.addEventListener('click', () => this.resetFilters(false));
      this.elements.reset?.addEventListener('click', () => this.resetFilters(true));

      this.elements.sort?.addEventListener('change', (event) => {
        this.sort = event.target.value;
        this.render();
      });

      this.elements.filterToggle?.addEventListener('click', () => {
        const isOpen = this.elements.filterPanel.classList.toggle('is-open');
        this.elements.filterToggle.setAttribute('aria-expanded', String(isOpen));
      });

      this.elements.filterClose?.addEventListener('click', () => {
        this.elements.filterPanel.classList.remove('is-open');
        this.elements.filterToggle?.setAttribute('aria-expanded', 'false');
      });

      this.elements.loadMore?.addEventListener('click', () => {
        this.visibleLimit += this.pageSize;
        this.renderCards();
      });

      this.elements.pdfDownload?.addEventListener('click', (event) => this.downloadPdf(event));
      this.elements.results?.addEventListener('click', (event) => this.handleResultAction(event));
    }

    async downloadPdf(event) {
      const link = event.currentTarget;
      const url = link.href;
      if (!url || link.dataset.downloading === 'true') return;

      event.preventDefault();
      const defaultLabel = link.textContent.trim();
      link.dataset.downloading = 'true';
      link.setAttribute('aria-busy', 'true');
      link.textContent = 'Preparing Download...';

      try {
        const response = await fetch(url, { credentials: 'omit', mode: 'cors' });
        if (!response.ok) throw new Error(`PDF download failed with status ${response.status}`);

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const download = document.createElement('a');
        download.href = objectUrl;
        download.download = link.getAttribute('download') || 'KILO-Exercise-Library.pdf';
        download.style.display = 'none';
        document.body.appendChild(download);
        download.click();
        download.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      } catch (error) {
        console.error('The PDF could not be downloaded automatically.', error);
        const fallback = document.createElement('a');
        fallback.href = url;
        fallback.download = link.getAttribute('download') || 'KILO-Exercise-Library.pdf';
        fallback.style.display = 'none';
        document.body.appendChild(fallback);
        fallback.click();
        fallback.remove();
      } finally {
        link.dataset.downloading = 'false';
        link.removeAttribute('aria-busy');
        link.textContent = defaultLabel;
      }
    }

    updateVariationSelection(input, selected) {
      if (!input.checked) {
        selected.delete(input.value);
        return;
      }

      const definition = this.filterDefinitions.find((filter) => filter.key === 'variationFilters');
      const option = definition?.options.find((candidate) => candidate.value === input.value);
      if (!option) {
        selected.add(input.value);
        return;
      }

      if (option.parent) {
        selected.delete(option.parent);
        this.setFilterInputChecked('variationFilters', option.parent, false);
      } else {
        definition.options
          .filter((candidate) => candidate.parent === option.value)
          .forEach((child) => {
            selected.delete(child.value);
            this.setFilterInputChecked('variationFilters', child.value, false);
          });
      }

      selected.add(option.value);
    }

    setFilterInputChecked(key, value, checked) {
      const input = this.elements.filterGroups.querySelector(
        `[data-filter-key="${CSS.escape(key)}"][value="${CSS.escape(value)}"]`
      );
      if (input) input.checked = checked;
    }

    clearSearchState(focusSearch) {
      this.query = '';
      if (this.elements.search) this.elements.search.value = '';
      if (this.elements.clearSearch) this.elements.clearSearch.hidden = true;
      if (focusSearch) this.elements.search?.focus();
    }

    clearFilterSelections() {
      Object.values(this.selected).forEach((set) => set.clear());
      this.elements.filterGroups?.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.checked = false;
      });
    }

    resetFilters(includeSearch) {
      this.clearFilterSelections();
      this.region = 'All Exercises';
      if (includeSearch) this.clearSearchState(false);
      this.visibleLimit = this.pageSize;
      this.renderRegions();
      this.render();
    }

    renderRegions() {
      const counts = new Map();
      this.data.forEach((record) => counts.set(record.region, (counts.get(record.region) || 0) + 1));
      const preferredOrder = [
        'Upper Body',
        'Lower Body',
        'Abdominals',
        'Olympic Lift Variations',
        'Plyometrics',
        'General Warm-Up'
      ];
      const regions = ['All Exercises', ...preferredOrder.filter((region) => counts.has(region))];
      this.elements.regionTabs.innerHTML = regions.map((region) => {
        const sampleCount = region === 'All Exercises' ? this.data.length : counts.get(region);
        const fullCount = this.fullCounts[region] || sampleCount;
        const count = this.access === 'full'
          ? fullCount.toLocaleString()
          : `${sampleCount.toLocaleString()}/${fullCount.toLocaleString()}`;
        return `
          <button
            class="kilo-el__region-button"
            type="button"
            role="tab"
            aria-selected="${this.region === region}"
            data-region="${escapeHtml(region)}"
          >
            ${escapeHtml(region)} <span class="kilo-el__region-count">${count}</span>
          </button>
        `;
      }).join('');
    }

    renderFilterGroups() {
      const filter = this.filterDefinitions[0];
      if (!filter) return;
      const options = filter.options.map((option) => {
        const unavailable = this.access !== 'full' && option.count === 0;
        const fullCount = option.fullCount || option.count;
        const count = this.access === 'full'
          ? option.count.toLocaleString()
          : `${option.count.toLocaleString()}/${fullCount.toLocaleString()}`;
        const availability = this.access === 'full'
          ? `${option.count.toLocaleString()} exercises`
          : unavailable
            ? `0 available in sample; ${fullCount.toLocaleString()} in complete library`
            : `${option.count.toLocaleString()} available in sample; ${fullCount.toLocaleString()} in complete library`;
        return `
          <label class="kilo-el__filter-option${option.isSubcategory ? ' kilo-el__filter-option--subcategory' : ''}${unavailable ? ' kilo-el__filter-option--unavailable' : ''}"${unavailable ? ` title="${escapeHtml(availability)}"` : ''}>
            <input type="checkbox" value="${escapeHtml(option.value)}" data-filter-key="${escapeHtml(filter.key)}"${unavailable ? ' disabled' : ''}>
            <span>${escapeHtml(option.label)}</span>
            <small aria-label="${escapeHtml(availability)}">${count}</small>
          </label>
        `;
      }).join('');
      const countNote = this.access === 'full'
        ? ''
        : '<p class="kilo-el__filter-count-note">Sample / Complete Library</p>';
      this.elements.filterGroups.innerHTML = `${countNote}<div class="kilo-el__filter-options kilo-el__filter-options--variations">${options}</div>`;
    }

    getFilteredData() {
      const queryTerms = normalize(this.query).split(' ').filter(Boolean);
      const isSearching = queryTerms.length > 0;
      const records = this.data.filter((record) => {
        if (!isSearching && this.region !== 'All Exercises' && record.region !== this.region) return false;

        const searchable = normalize([
          record.name,
          record.region,
          record.family,
          record.variation
        ].join(' '));
        if (isSearching) return queryTerms.every((term) => searchable.includes(term));

        return FILTERS.every((filter) => {
          const selected = this.selected[filter.key];
          if (!selected.size) return true;
          const values = Array.isArray(record[filter.key]) ? record[filter.key] : [record[filter.key]];
          return values.some((value) => selected.has(value));
        });
      });

      if (this.sort === 'az') records.sort((a, b) => a.name.localeCompare(b.name));
      if (this.sort === 'za') records.sort((a, b) => b.name.localeCompare(a.name));
      if (this.sort === 'source') records.sort((a, b) => a.sourceIndex - b.sourceIndex);
      return records;
    }

    render() {
      this.filtered = this.getFilteredData();
      this.renderHeading();
      this.renderActiveFilters();
      this.renderCards();
    }

    renderHeading() {
      const exerciseLabel = this.filtered.length === 1 ? 'exercise' : 'exercises';
      if (this.access === 'full') {
        this.elements.count.textContent = `${this.filtered.length.toLocaleString()} ${exerciseLabel} · Complete Library`;
      } else {
        const hasActiveFilters = FILTERS.some((filter) => this.selected[filter.key].size > 0);
        if (!this.query && !hasActiveFilters) {
          const fullCount = this.fullCounts[this.region] || this.filtered.length;
          this.elements.count.textContent = `Showing ${this.filtered.length.toLocaleString()} of ${fullCount.toLocaleString()} ${exerciseLabel} · Public Preview`;
        } else {
          this.elements.count.textContent = `${this.filtered.length.toLocaleString()} sample ${exerciseLabel} · Public Preview`;
        }
      }
      if (this.query) {
        this.elements.heading.textContent = `Results for “${this.query}”`;
      } else {
        this.elements.heading.textContent = this.region;
      }
    }

    renderActiveFilters() {
      const active = [];
      FILTERS.forEach((filter) => {
        this.selected[filter.key].forEach((value) => active.push({ key: filter.key, value }));
      });
      this.elements.activeFilters.hidden = active.length === 0;
      this.elements.activeFilters.innerHTML = active.map((filter) => `
        <button
          class="kilo-el__active-filter"
          type="button"
          data-remove-filter
          data-filter-key="${escapeHtml(filter.key)}"
          data-filter-value="${escapeHtml(filter.value)}"
          aria-label="Remove ${escapeHtml(filter.value)} filter"
        >
          ${escapeHtml(filter.value)} <span aria-hidden="true">×</span>
        </button>
      `).join('');

      if (active.length) {
        this.elements.activeFilterCount.hidden = false;
        this.elements.activeFilterCount.textContent = active.length;
      } else {
        this.elements.activeFilterCount.hidden = true;
      }
    }

    renderCards() {
      const visible = this.filtered.slice(0, this.visibleLimit);
      this.elements.empty.hidden = this.filtered.length !== 0;
      this.elements.results.hidden = this.filtered.length === 0;
      this.elements.results.innerHTML = visible.map((record) => this.cardMarkup(record)).join('');
      this.elements.loadMoreWrap.hidden = this.visibleLimit >= this.filtered.length;
      if (!this.elements.loadMoreWrap.hidden) {
        const remaining = this.filtered.length - this.visibleLimit;
        this.elements.loadMore.textContent = `Load More Exercises (${remaining.toLocaleString()} remaining)`;
      }
    }

    cardMarkup(record) {
      const id = escapeHtml(record.id);
      const chips = [
        record.region,
        record.variation,
        ...(record.equipment || []).slice(0, 1),
        ...(record.position || []).slice(0, 1),
        ...(record.grip || []).slice(0, 1),
        ...(record.laterality || []).slice(0, 1)
      ].filter((value, index, array) => value && array.indexOf(value) === index).slice(0, 6);
      const validVideoId = /^[A-Za-z0-9_-]+$/.test(record.videoId || '') ? record.videoId : '';
      const hasVideo = Boolean(validVideoId && record.url);
      const thumbnail = hasVideo ? `https://i.ytimg.com/vi/${validVideoId}/hqdefault.jpg` : '';
      const thumbnailMarkup = hasVideo
        ? `<button class="kilo-el__thumbnail" type="button" data-video-toggle aria-expanded="false" aria-label="Watch ${escapeHtml(record.name)}">
            <img src="${thumbnail}" alt="" loading="lazy" width="480" height="270">
            <span class="kilo-el__play" aria-hidden="true"></span>
          </button>`
        : `<div class="kilo-el__thumbnail kilo-el__thumbnail--unavailable" aria-label="Demonstration unavailable">
            <span>Demonstration<br>unavailable</span>
          </div>`;
      const viewAction = hasVideo
        ? `<button class="kilo-el__card-button" type="button" data-video-toggle aria-expanded="false">
            ${ICONS.play}<span>View Demo</span>
          </button>`
        : '';
      const linkAction = hasVideo
        ? `<button class="kilo-el__card-button" type="button" data-copy-value="${escapeHtml(record.url)}" data-default-label="Copy Link">
            ${ICONS.link}<span>Copy Link</span>
          </button>`
        : '<span class="kilo-el__unavailable-status">Demonstration unavailable</span>';

      return `
        <article class="kilo-el__card" data-exercise-id="${id}" data-video-id="${escapeHtml(validVideoId)}">
          <div class="kilo-el__card-main">
            ${thumbnailMarkup}
            <div class="kilo-el__card-info">
              <h3>${escapeHtml(record.name)}</h3>
              <div class="kilo-el__chips">${chips.map((chip) => `<span class="kilo-el__chip">${escapeHtml(chip)}</span>`).join('')}</div>
            </div>
            <div class="kilo-el__card-actions">
              ${viewAction}
              <button class="kilo-el__card-button" type="button" data-copy-value="${escapeHtml(record.name)}" data-default-label="Copy Name">
                ${ICONS.copy}<span>Copy Name</span>
              </button>
              ${linkAction}
            </div>
          </div>
          <div class="kilo-el__video-panel" data-video-panel hidden></div>
        </article>
      `;
    }

    handleResultAction(event) {
      const copyButton = event.target.closest('[data-copy-value]');
      if (copyButton) {
        this.copyValue(copyButton.dataset.copyValue, copyButton);
        return;
      }

      const videoButton = event.target.closest('[data-video-toggle]');
      if (!videoButton) return;
      const card = videoButton.closest('[data-exercise-id]');
      const panel = card.querySelector('[data-video-panel]');
      const videoId = card.dataset.videoId;
      const isOpen = !panel.hidden;
      card.querySelectorAll('[data-video-toggle]').forEach((button) => button.setAttribute('aria-expanded', String(!isOpen)));

      if (isOpen) {
        panel.hidden = true;
        panel.innerHTML = '';
        return;
      }

      if (!/^[A-Za-z0-9_-]+$/.test(videoId)) return;
      const title = card.querySelector('h3')?.textContent || 'KILO exercise demonstration';
      panel.innerHTML = `
        <div class="kilo-el__video-frame">
          <iframe
            src="https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1"
            title="${escapeHtml(title)}"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
          ></iframe>
        </div>
      `;
      panel.hidden = false;
      panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      this.dispatchAction('view_demo', card.dataset.exerciseId);
    }

    legacyCopy(value) {
      const textarea = document.createElement('textarea');
      const previousFocus = document.activeElement;
      textarea.value = value;
      textarea.setAttribute('readonly', '');
      textarea.setAttribute('aria-hidden', 'true');
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '-9999px';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus({ preventScroll: true });
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);

      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch (error) {
        copied = false;
      }

      textarea.remove();
      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus({ preventScroll: true });
      }
      return copied;
    }

    async copyValue(value, button) {
      let copied = this.legacyCopy(value);

      if (!copied && navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(value);
          copied = true;
        } catch (error) {
          copied = false;
        }
      }

      const label = button.querySelector('span');
      label.textContent = copied ? 'Copied' : 'Copy failed';
      window.setTimeout(() => { label.textContent = button.dataset.defaultLabel; }, 1600);

      if (copied) {
        this.dispatchAction(button.dataset.defaultLabel === 'Copy Link' ? 'copy_link' : 'copy_name', value);
      } else {
        console.error('Unable to copy exercise information.');
      }
    }

    dispatchAction(action, value) {
      document.dispatchEvent(new CustomEvent('kilo:exercise-library-action', {
        detail: { action, value }
      }));
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-exercise-library]').forEach((root) => {
      if (root.dataset.initialized) return;
      root.dataset.initialized = 'true';
      new KiloExerciseLibrary(root);
    });
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
